import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Sparkles, ShieldCheck, TrendingUp } from "lucide-react";
import { BUSINESS_TYPES } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Hero() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.business_type) {
      toast.error("Please fill all required fields.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, { ...form, source: "hero_form" });
      toast.success("Thanks! We'll reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", business_type: "", message: "" });
    } catch (err) {
      toast.error("Couldn't submit. Please try again.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
      {/* Background accents */}
      <div className="blur-blob bg-[#CBBCE0]/50 w-[420px] h-[420px] -top-24 -right-32" />
      <div className="blur-blob bg-[#D1A5A5]/40 w-[360px] h-[360px] -bottom-24 -left-24" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(4,16,71,0.03)_1px,_transparent_1px)] bg-[length:28px_28px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left */}
        <div className="lg:col-span-7 ddn-fade">
          <span className="eyebrow inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            Premium Digital Marketing · Ahmedabad
          </span>
          <h1 className="font-display mt-5 text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#041047]">
            Building Brands. <br className="hidden md:block" />
            <span className="italic font-medium">Driving Growth</span> Through Digital Marketing.
          </h1>
          <p className="mt-7 text-lg md:text-xl text-[#475569] max-w-2xl leading-relaxed">
            DDigital Studios helps businesses grow online with powerful SEO, Google Ads,
            Meta Ads, social media marketing and high-performing websites designed to
            generate real results.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-5">
            <a href="#services" data-testid="hero-explore-btn" className="btn-primary">
              Explore Services <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#portfolio" data-testid="hero-portfolio-btn" className="btn-secondary">
              View Our Work
            </a>
          </div>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-xl">
            <div className="border-l-2 border-[#D4AF37] pl-4">
              <div className="font-display text-3xl text-[#041047]">120+</div>
              <div className="text-xs uppercase tracking-wider text-[#475569] mt-1">Brands Scaled</div>
            </div>
            <div className="border-l-2 border-[#D4AF37] pl-4">
              <div className="font-display text-3xl text-[#041047]">4.6x</div>
              <div className="text-xs uppercase tracking-wider text-[#475569] mt-1">Avg. ROAS</div>
            </div>
            <div className="border-l-2 border-[#D4AF37] pl-4">
              <div className="font-display text-3xl text-[#041047]">98%</div>
              <div className="text-xs uppercase tracking-wider text-[#475569] mt-1">Client Retention</div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#475569]">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Transparent reporting
            </span>
            <span className="inline-flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#D4AF37]" /> Result-driven strategies
            </span>
          </div>
        </div>

        {/* Right: Form */}
        <div className="lg:col-span-5 ddn-fade" style={{ animationDelay: "120ms" }}>
          <div className="bg-white border border-[#041047]/10 p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(4,16,71,0.35)] relative">
            <div className="absolute -top-3 left-8 bg-[#D4AF37] text-[#1a1200] text-[0.65rem] tracking-[0.2em] font-bold uppercase px-3 py-1">
              Free Consultation
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-[#041047]">
              Tell us about your business
            </h3>
            <p className="text-sm text-[#475569] mt-2">
              Get a tailored growth plan within 24 hours — no obligation.
            </p>

            <form
              data-testid="hero-lead-form"
              onSubmit={onSubmit}
              className="mt-6 flex flex-col gap-5"
            >
              <input
                data-testid="form-name"
                className="ddn-input"
                name="name"
                placeholder="Full Name *"
                value={form.name}
                onChange={onChange}
                required
              />
              <input
                data-testid="form-email"
                className="ddn-input"
                name="email"
                type="email"
                placeholder="Email Address *"
                value={form.email}
                onChange={onChange}
                required
              />
              <input
                data-testid="form-phone"
                className="ddn-input"
                name="phone"
                placeholder="Phone Number *"
                value={form.phone}
                onChange={onChange}
                required
              />
              <select
                data-testid="form-business-type"
                className="ddn-input bg-transparent appearance-none cursor-pointer"
                name="business_type"
                value={form.business_type}
                onChange={onChange}
                required
              >
                <option value="" disabled>Business Type *</option>
                {BUSINESS_TYPES.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              <textarea
                data-testid="form-message"
                className="ddn-input ddn-textarea"
                name="message"
                placeholder="Tell us about your goals (optional)"
                value={form.message}
                onChange={onChange}
                rows={2}
              />
              <button
                data-testid="form-submit"
                type="submit"
                disabled={submitting}
                className="btn-primary mt-2 disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Get Started"}
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-[#475569]/80">
                By submitting, you agree to be contacted by DDigital Studios.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
